// "Send to Uptick" card on the test detail page (uptick_plan.md §13.4/§14).
// Renders nothing unless the org has an Uptick connection. Shows the job
// badge (synced / partial / failed), and for un-pushed tests the three-step
// live picker: property (text search) → asset (pre-filtered to battery asset
// types, "show all" lifts it) → work order (service task). A technician's
// matching tick on the line is kept and the readings added; a disagreeing
// result, or another test's, stops for a person's confirmation (§12, rule 1).

import { useState } from "react";
import { Link } from "react-router-dom";
import { UptickSuggestionView } from "./UptickSuggestion.tsx";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  uptickApi, uptickPushApi,
  type UptickPropertyHit, type UptickAssetHit, type UptickServicetaskHit, type UptickDocOutcome,
} from "../lib/api.ts";

function docLine(label: string, d: UptickDocOutcome | undefined) {
  if (!d || d.skipped) return null;
  if (d.ok) return <span>{label}: uploaded{d.existed ? " (already there)" : ""}</span>;
  return <span className="err">{label}: {d.error ?? "failed"}</span>;
}

export function UptickPushCard(props: { orgId: string; testId: string }) {
  const { orgId, testId } = props;
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [prop, setProp] = useState<UptickPropertyHit | null>(null);
  const [asset, setAsset] = useState<UptickAssetHit | null>(null);
  const [picked, setPicked] = useState<UptickServicetaskHit | null>(null);
  const [showAll, setShowAll] = useState(false);

  const status = useQuery({
    queryKey: ["org", orgId, "uptick"],
    queryFn: () => uptickApi.status(orgId),
    staleTime: 5 * 60_000,
    retry: false,
  });
  const connected = status.data?.connected === true;

  const job = useQuery({
    queryKey: ["org", orgId, "uptick-job", testId],
    queryFn: () => uptickPushApi.jobForTest(orgId, testId),
    enabled: connected,
    retry: false,
  });
  const j = job.data?.jobs?.[0] ?? null;
  const settled = j?.status === "done" || j?.status === "partial";

  const hits = useQuery({
    queryKey: ["org", orgId, "uptick-props", q],
    queryFn: () => uptickPushApi.searchProperties(orgId, q),
    enabled: connected && !settled && !prop,
    retry: false,
  });
  const assetList = useQuery({
    queryKey: ["org", orgId, "uptick-passets", prop?.propertyId, showAll],
    queryFn: () => uptickPushApi.propertyAssets(orgId, prop!.propertyId, showAll),
    enabled: connected && !!prop && !asset,
    retry: false,
  });
  const stList = useQuery({
    queryKey: ["org", orgId, "uptick-ast", asset?.assetId],
    queryFn: () => uptickPushApi.assetServicetasks(orgId, asset!.assetId),
    enabled: connected && !!asset,
    retry: false,
  });

  const push = useMutation({
    mutationFn: (arg: number | { servicetaskId: number; documentsOnly?: true; confirmOverwrite?: true }) =>
      typeof arg === "number"
        ? uptickPushApi.push(orgId, testId, arg)
        : uptickPushApi.push(orgId, testId, arg.servicetaskId,
            { documentsOnly: arg.documentsOnly, confirmOverwrite: arg.confirmOverwrite }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["org", orgId, "uptick-job", testId] }),
    onError: () => qc.invalidateQueries({ queryKey: ["org", orgId, "uptick-job", testId] }),
  });

  if (!connected) return null;

  const aLabel = (h: UptickAssetHit) =>
    `${h.ref ? `${h.ref} — ` : ""}${h.label ?? `asset ${h.assetId}`}${h.typeName ? ` · ${h.typeName}` : ""}`;
  const stLabel = (h: UptickServicetaskHit) =>
    `Work order T-${h.taskId ?? "?"} · service task #${h.servicetaskId}${h.result ? ` (done: ${h.result})` : " (open)"}`;
  const filterNote = assetList.data?.filtered === "bt1851"
    ? "Showing asset types wired to BT1851 questions"
    : assetList.data?.filtered === "name" ? "Showing battery-named asset types" : null;

  return (
    <div className="card" style={{ maxWidth: "44em", marginTop: "1em" }}>
      <h4 style={{ marginTop: 0 }}>Uptick</h4>
      {settled && (
        <p>
          <span className="pill pass">Synced</span>{" "}
          <span className="muted">
            service task #{j!.servicetask_id}
            {j!.detail?.task_id ? ` on T-${j!.detail.task_id}` : ""}
            {j!.detail?.mode === "verdict-only" ? " · verdict only (no BT1851 questions on this form)" : ""}
            {j!.detail?.mode === "answers" ? ` · ${j!.detail.answers} answer${j!.detail.answers === 1 ? "" : "s"}` : ""}
            {j!.detail?.verdict_kept ? " · the technician's matching tick was kept" : ""}
            {j!.completed_at ? ` · ${new Date(j!.completed_at).toLocaleString()}` : ""}
          </span>
        </p>
      )}
      {settled && (j!.detail?.doc_task || j!.detail?.doc_asset) && (
        <p className="muted" style={{ fontSize: "0.9em", display: "flex", gap: "1em", flexWrap: "wrap" }}>
          {docLine("Work-order document", j!.detail?.doc_task)}
          {docLine("Asset document", j!.detail?.doc_asset)}
        </p>
      )}
      {j?.status === "partial" && (
        <p>
          <span className="err">A document upload failed (attempt {j.attempts}).</span>{" "}
          <button type="button" disabled={push.isPending || !j.servicetask_id}
            onClick={() => j.servicetask_id && push.mutate(j.servicetask_id)}>
            {push.isPending ? "Retrying…" : "Retry upload"}
          </button>
        </p>
      )}
      {j?.status === "done" && j.servicetask_id && (
        <p className="muted" style={{ fontSize: "0.9em" }}>
          <button type="button" disabled={push.isPending}
            onClick={() => push.mutate({ servicetaskId: j.servicetask_id!, documentsOnly: true })}>
            {push.isPending ? "Sending…" : "Re-send PDF"}
          </button>{" "}
          re-uploads the report to Uptick per the current document setting.
        </p>
      )}
      {j?.status === "failed" && (
        <p className="err">Last push failed (attempt {j.attempts}): {j.last_error}</p>
      )}
      {j?.status === "needs_confirmation" && j.servicetask_id && (() => {
        const c = j.detail?.conflict;
        return (
          <div style={{ border: "1px solid currentColor", borderRadius: "6px", padding: "0.6em 0.8em", margin: "0.5em 0" }}>
            <p className="err" style={{ marginTop: 0 }}>
              Work order T-{j.detail?.task_id ?? "?"} (service task #{j.servicetask_id}) already has result{" "}
              <strong>{c?.result ?? "?"}</strong>
              {c?.performed_date ? ` from ${new Date(c.performed_date).toLocaleDateString()}` : ""}
              {c?.by_test_id
                ? <> — sent from another BT-1851 test, <Link to={`/tests/${c.by_test_id}`}>{c.by_test_name ?? c.by_test_id}</Link></>
                : " — ticked in Uptick"}
              {c?.this_verdict ? <>; this test measured <strong>{c.this_verdict}</strong></> : ""}.
            </p>
            <p className="muted" style={{ fontSize: "0.9em" }}>
              Sending this result replaces it. Check this is the right battery set and which result is correct, or
              choose another work order below.
            </p>
            <button type="button" disabled={push.isPending}
              onClick={() => push.mutate({ servicetaskId: j.servicetask_id!, confirmOverwrite: true })}>
              {push.isPending ? "Replacing…" : "Replace the result"}
            </button>
          </div>
        );
      })()}
      {!settled && (
        <>
          {j?.status === "unmatched" && (
            <p className="muted" style={{ fontSize: "0.9em" }}>
              <span className="pill">Awaiting work order</span> Auto-push is on,
              but this result arrived without an Uptick work-order reference.
            </p>
          )}
          {j?.status === "unmatched" && j.suggestion && (
            <UptickSuggestionView
              suggestion={j.suggestion}
              sending={push.isPending}
              onSend={(st) => push.mutate(st)}
            />
          )}
          <p className="muted" style={{ fontSize: "0.9em" }}>
            Push this result onto an Uptick work order: property → asset →
            work order. The verdict, any BT1851 readings and the report PDF
            are written to the chosen service task.
          </p>
          {!prop && (
            <>
              <input
                placeholder="Search properties…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                style={{ width: "100%", marginBottom: "0.5em" }}
              />
              {hits.data && hits.data.properties.length > 0 && (
                <ul style={{ listStyle: "none", padding: 0, margin: 0, maxHeight: "14em", overflowY: "auto" }}>
                  {hits.data.properties.map((h) => (
                    <li key={h.propertyId}>
                      <button type="button" style={{ width: "100%", textAlign: "left", margin: "0.15em 0" }}
                        onClick={() => { setProp(h); setAsset(null); setPicked(null); setShowAll(false); }}>
                        {h.label ?? `property ${h.propertyId}`}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
          {prop && (
            <p>
              Property: <strong>{prop.label ?? prop.propertyId}</strong>{" "}
              <button type="button" onClick={() => { setProp(null); setAsset(null); setPicked(null); }}>change</button>
            </p>
          )}
          {prop && !asset && (
            assetList.isPending ? <p className="muted">Loading assets…</p> :
            assetList.isError ? <p className="err">{(assetList.error as Error).message}</p> :
            <>
              {assetList.data && assetList.data.assets.length > 0 ? (
                <ul style={{ listStyle: "none", padding: 0, margin: 0, maxHeight: "14em", overflowY: "auto" }}>
                  {assetList.data.assets.map((h) => (
                    <li key={h.assetId}>
                      <button type="button" style={{ width: "100%", textAlign: "left", margin: "0.15em 0" }}
                        onClick={() => { setAsset(h); setPicked(null); }}>
                        {aLabel(h)}
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="muted">
                  {filterNote ? "No active battery assets on this property." : "No active assets on this property."}
                </p>
              )}
              {filterNote && (
                <p className="muted" style={{ fontSize: "0.85em" }}>
                  {filterNote}.{" "}
                  <button type="button" onClick={() => setShowAll(true)}>Show all asset types</button>
                </p>
              )}
              {!filterNote && showAll && (
                <p className="muted" style={{ fontSize: "0.85em" }}>
                  All asset types (first 200, newest first).{" "}
                  <button type="button" onClick={() => setShowAll(false)}>Battery types only</button>
                </p>
              )}
            </>
          )}
          {asset && (
            <p>
              Asset: <strong>{aLabel(asset)}</strong>{" "}
              <button type="button" onClick={() => { setAsset(null); setPicked(null); }}>change</button>
            </p>
          )}
          {asset && stList.isPending && <p className="muted">Loading work orders…</p>}
          {asset && stList.data && !picked && (
            stList.data.servicetasks.length > 0 ? (
              <ul style={{ listStyle: "none", padding: 0, margin: 0, maxHeight: "10em", overflowY: "auto" }}>
                {stList.data.servicetasks.map((h) => (
                  <li key={h.servicetaskId}>
                    <button type="button" style={{ width: "100%", textAlign: "left", margin: "0.15em 0" }}
                      onClick={() => setPicked(h)}>
                      {stLabel(h)}
                    </button>
                  </li>
                ))}
              </ul>
            ) : <p className="muted">No work orders on this asset. Add it to a task in Uptick first.</p>
          )}
          {picked && (
            <p>
              Target: <strong>{stLabel(picked)}</strong>{" "}
              <button type="button" onClick={() => setPicked(null)}>change</button>
            </p>
          )}
          <button
            type="button"
            disabled={!picked || push.isPending}
            onClick={() => picked && push.mutate(picked.servicetaskId)}
          >
            {push.isPending ? "Pushing…" : "Send result to Uptick"}
          </button>
        </>
      )}
      {push.isError && !(push.error as Error).message.startsWith("result-exists") && (
        <p className="err">{(push.error as Error).message}</p>
      )}
      {push.isSuccess && (
        <div className="muted" style={{ fontSize: "0.9em" }}>
          <p>
            {push.data.verdict_kept
              ? `Verdict ${push.data.verdict} was already ticked in Uptick — kept; ${push.data.answers} answer${push.data.answers === 1 ? "" : "s"} and the report added.`
              : push.data.mode === "verdict-only"
              ? `Verdict ${push.data.verdict} written (no BT1851 questions on this form).`
              : `Verdict ${push.data.verdict} + ${push.data.answers} answer${push.data.answers === 1 ? "" : "s"}${push.data.answers_updated ? ` (${push.data.answers_updated} updated)` : ""}.`}
          </p>
          <p style={{ display: "flex", gap: "1em", flexWrap: "wrap" }}>
            {docLine("Work-order document", push.data.documents.task)}
            {docLine("Asset document", push.data.documents.asset)}
          </p>
          {push.data.warnings.length > 0 && (
            <p style={{ fontSize: "0.85em" }}>Notes: {push.data.warnings.join(" · ")}</p>
          )}
        </div>
      )}
    </div>
  );
}
